import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Settings } from "lucide-react";
import { AuntieAvatar, UncAvatar, OleGirlAvatar, YKAvatar } from "../components/Avatars";

const PERSONAS = [
  {
    id: "auntie",
    name: "Auntie™",
    description: "Warm, nurturing, validating. Like that aunt who always has time for you.",
    tags: ["Nurturing", "Validating"],
    tagColors: ["bg-primary/20 text-primary", "bg-orange-100 text-orange-700"],
    Avatar: AuntieAvatar,
    cardClass: "auntie",
  },
  {
    id: "unc",
    name: "Unc™",
    description: "Calm, grounded, protective. Like that uncle who's got your back.",
    tags: ["Grounded", "Protective"],
    tagColors: ["bg-amber-100 text-amber-800", "bg-orange-100 text-orange-700"],
    Avatar: UncAvatar,
    cardClass: "unc",
  },
  {
    id: "olegirl",
    name: "Ole Girl™",
    description: "Real, loyal, culturally fluent. The friend everyone trusts and vents to.",
    tags: ["Real Talk", "Loyal"],
    tagColors: ["bg-orange-100 text-orange-700", "bg-amber-100 text-amber-800"],
    Avatar: OleGirlAvatar,
    cardClass: "olegirl",
  },
  {
    id: "yk",
    name: "YK™",
    subtitle: "Young King",
    description: "Direct, grounded, growth-focused. Been through it, still standing.",
    tags: ["Growth", "Real"],
    tagColors: ["bg-amber-100 text-amber-800", "bg-primary/20 text-primary"],
    Avatar: YKAvatar,
    cardClass: "yk",
  },
];

export default function PersonaSelectPage() {
  const { user, selectPersona } = useAuth();
  const navigate = useNavigate();

  const handleSelect = async (persona) => {
    try {
      await selectPersona(persona);
      const personaName = PERSONAS.find(p => p.id === persona)?.name || persona;
      toast.success(`${personaName} is ready to listen`);
      navigate("/vent");
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const isWednesday = new Date().getDay() === 3;
  const canAccessWednesday = user?.subscription_tier !== "free" && isWednesday;

  return (
    <div className="min-h-screen flex flex-col p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex-1 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl font-bold">
              Who do you want to talk to?
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Choose who'll listen today
            </p>
          </div>
          <button
            onClick={() => navigate("/profile")}
            className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
            data-testid="profile-btn"
          >
            <Settings className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Vents remaining */}
        {user?.subscription_tier === "free" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-4 mb-5 border border-border"
          >
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">Free vents remaining</span>
              <span className="font-serif text-xl font-bold text-primary">
                {user.free_vents_remaining}
              </span>
            </div>
          </motion.div>
        )}

        {/* Wednesday Vent Banner */}
        {canAccessWednesday && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="wednesday-accent rounded-2xl p-4 mb-5 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => navigate("/vent", { state: { isWednesdayVent: true } })}
            data-testid="wednesday-vent-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-xl">✨</span>
              </div>
              <div>
                <h3 className="font-semibold">Wednesday Vent</h3>
                <p className="text-sm opacity-90">For the Strong Friend — Extended session</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Persona Cards - 2x2 Grid */}
        <div className="flex-1 grid grid-cols-2 gap-3">
          {PERSONAS.map((persona, index) => {
            const AvatarComponent = persona.Avatar;
            return (
              <motion.div
                key={persona.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                onClick={() => handleSelect(persona.id)}
                className={`persona-card ${persona.cardClass} bg-card rounded-2xl p-4 border border-border cursor-pointer hover:border-primary/40 active:scale-[0.98] transition-all`}
                data-testid={`${persona.id}-card`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="mb-3">
                    <AvatarComponent size="md" />
                  </div>
                  <h3 className="font-serif text-lg font-bold">{persona.name}</h3>
                  {persona.subtitle && (
                    <p className="text-xs text-muted-foreground">{persona.subtitle}</p>
                  )}
                  <p className="text-muted-foreground text-xs leading-relaxed mt-2 line-clamp-2">
                    {persona.description}
                  </p>
                  <div className="mt-3 flex flex-wrap justify-center gap-1">
                    {persona.tags.map((tag, i) => (
                      <span
                        key={tag}
                        className={`text-xs px-2 py-0.5 rounded-full ${persona.tagColors[i]}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Upgrade prompt for free users */}
        {user?.subscription_tier === "free" && user?.free_vents_remaining <= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-4 text-center"
          >
            <button
              onClick={() => navigate("/pricing")}
              className="text-primary hover:underline text-sm"
              data-testid="upgrade-prompt"
            >
              Running low on vents? Upgrade for unlimited access →
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
